import { useCallback, useRef, useState } from "react";

interface UseFormSubmissionReturn {
  registerSubmit: (submitFn: () => void) => void;
  submit: () => void;
  isFormReady: boolean;
  isDirty: boolean;
  updateDirtyState: (isDirty: boolean) => void;
  forceCleanState: () => void;
}

export const useFormSubmission = (): UseFormSubmissionReturn => {
  const submitFnRef = useRef<(() => void) | null>(null);
  const isFormReadyRef = useRef(false);
  const [isDirty, setIsDirty] = useState(false);

  const registerSubmit = useCallback((submitFn: () => void) => {
    submitFnRef.current = submitFn;
    isFormReadyRef.current = true;
  }, []);

  const updateDirtyState = useCallback((newIsDirty: boolean) => {
    setIsDirty((prev) => {
      if (prev !== newIsDirty) {
        return newIsDirty;
      }
      return prev;
    });
  }, []);

  const forceCleanState = useCallback(() => {
    setIsDirty(false);
  }, []);

  const submit = useCallback(() => {
    if (submitFnRef.current) {
      try {
        submitFnRef.current();
      } catch (error) {
        // Gracefully handle errors - don't let them bubble up
        console.error("Form submission error:", error);
      }
    }
  }, []);

  return {
    registerSubmit,
    submit,
    get isFormReady() {
      return isFormReadyRef.current;
    },
    isDirty,
    updateDirtyState,
    forceCleanState,
  };
};
